import { type FC, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { KycButton } from "src/app/verification/components/KycButton";
import sumSubLogo from "src/assets/img/logo/sumsub.png";
import { Checking, Heading, Loading } from "src/components";
import { Routes } from "src/routes";
import { useUserQuery } from "src/serverApi";
import { useTranslation } from "src/translations";
import { UserVerificationTypeEnum } from "src/types";
import { logGtmEvent, useComponentDidMount } from "src/utils";
import { DO_BANK_ID_REDIRECT } from "../constants";
import { VerificationSubTypeEnum } from "../enums";
import { KYC_SELECTION_ROUTE } from "../utils";

export const Kyc: FC = () => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const checking = searchParams.get("checking");
    const { language } = i18n;

    const { data: user, isLoading: isUserLoading } = useUserQuery(undefined, { pollingInterval: checking ? 5000 : undefined });

    useComponentDidMount(() => {
        logGtmEvent({ event: "user_kyc_entered", onlyOnce: true });
    });

    const hasKyc = user?.verification.some((verification) => verification.type === UserVerificationTypeEnum.KYC);

    useEffect(() => {
        if (hasKyc) {
            logGtmEvent({ event: "user_kyc_verified", onlyOnce: true });
        }
    }, [hasKyc]);

    // The buttons should be 1. Bank iD, 2. SumSub for Czech language; 1. SumSub, 2. Bank iD for other languages
    const buttons = useMemo(() => {
        const sumSubFlow = () => {
            navigate(
                Routes.verifications.fillPathParams({
                    verificationType: UserVerificationTypeEnum.KYC,
                    verificationSubType: VerificationSubTypeEnum.SUMSUB,
                }),
            );
        };

        const bankIdFlow = () => {
            sessionStorage.setItem(DO_BANK_ID_REDIRECT, "true");
            navigate(
                Routes.verifications.fillPathParams({
                    verificationType: UserVerificationTypeEnum.KYC,
                    verificationSubType: VerificationSubTypeEnum.BANKID,
                }),
            );
        };

        const bankIdButton = {
            key: "BankId",
            component: (
                <div className="grid justify-items-center gap-4" key="bankid">
                    <KycButton
                        title={t("verification.KYC.bankId")}
                        description={t("verification.KYC.bankIdDescription")}
                        image={<BankIdLogo />}
                        onClick={bankIdFlow}
                    />
                </div>
            ),
        };

        const sumSubButton = {
            key: "SumSub",
            component: (
                <div className="grid justify-items-center gap-4" key="sumsub">
                    <KycButton
                        title={t("verification.KYC.sumSub")}
                        description={t("verification.KYC.sumSubDescription")}
                        image={<img src={sumSubLogo} alt="Sumsub" className="max-h-[26px]" />}
                        onClick={sumSubFlow}
                    />
                </div>
            ),
        };

        return language === "cs" ? [bankIdButton, sumSubButton] : [sumSubButton, bankIdButton];
    }, [language, t, navigate]);

    if (isUserLoading) {
        return <Loading />;
    }

    return (
        <div className="grid size-full items-center justify-center">
            <div className="mt-12 grid size-full items-center justify-center">
                {hasKyc ? (
                    <>
                        <Heading level={2} className="mb-4 text-center">
                            {t("verification.KYC.completed")}
                        </Heading>
                        <Heading level={5} className="text-center">
                            {t("verification.KYC.redirect")}
                        </Heading>
                    </>
                ) : checking ? (
                    <>
                        <Heading level={2} className="mb-4 text-center">
                            {t("verification.KYC.title")}
                        </Heading>
                        <Checking title={t("verification.KYC.checkingStatus")} linkToStopChecking={KYC_SELECTION_ROUTE} />
                    </>
                ) : (
                    <>
                        <Heading level={2} className="mb-4 text-center">
                            {t("verification.KYC.title")}
                        </Heading>

                        <p className="whitespace-pre-wrap">{t("verification.KYC.information")}</p>

                        <div className="grid gap-8 p-4">
                            {buttons.map((button) => (
                                <div key={button.key}>{button.component}</div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const BankIdLogo = () => (
    <svg width="118" height="48" viewBox="0 0 118 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="Group 8">
            <g id="bankid_logo 1" clipPath="url(#clip0_2114_158)" transform="matrix(1.0000057,0,0,1.0000057,12.231114,-2.8676654e-4)">
                <path
                    id="Vector"
                    d="m 91.815,18.558 h 4.0742 c 3.2101,0 4.8148,2.6686 4.8148,5.3126 0,2.6686 -1.6047,5.3125 -4.8148,5.3125 H 91.815 v -1.2355 h 1.7285 V 19.7935 H 91.815 Z m -2.3456,13.9609 h 6.5434 c 5.8522,0 8.7662,-4.3241 8.7662,-8.6483 0,-4.3242 -2.914,-8.6484 -8.7662,-8.6484 H 89.4694 Z M 82.8027,18.558 h 3.7038 v -3.3358 h -3.7038 z m 0,1.2355 h 3.7038 v 12.7254 h -3.7038 z m -15.408,12.7254 v -4.25 h 1.2345 l 3.7042,4.25 h 3.6791 V 31.2835 L 71.3457,26.045 75.8888,21.0289 V 19.7935 H 72.358 l -3.7288,4.25 v 1.705 H 67.3947 V 15.2222 H 63.691 v 17.2967 z m -10.2474,0 h 3.7039 v -7.7094 c 0,-3.1875 -2.3211,-5.2631 -4.9138,-5.2631 -1.0865,0 -2.2224,0.3706 -3.2347,1.1613 v 1.4332 H 51.468 v -2.3474 h -3.21 v 12.7254 h 3.7039 v -7.0422 c 0,-1.7791 1.3581,-2.7181 2.6914,-2.7181 1.2593,0 2.494,0.8402 2.494,2.5945 z M 38.4306,22.7092 c 1.6545,0 3.0372,1.3343 3.0372,3.4347 0,2.1002 -1.3827,3.4346 -3.0372,3.4346 -1.6544,0 -3.1359,-1.3344 -3.1359,-3.4346 0,-2.1004 1.4815,-3.4347 3.1359,-3.4347 z m -6.9632,3.4347 c 0,4.1017 2.9384,6.6222 5.8521,6.6222 1.1605,0 2.3211,-0.3954 3.2841,-1.285 v -1.3096 h 1.2346 v 2.3474 h 3.457 V 19.7935 h -3.457 v 2.3474 h -1.2346 v -1.3096 c -0.963,-0.8896 -2.1236,-1.2849 -3.2841,-1.2849 -2.9137,0 -5.8521,2.5204 -5.8521,6.5975 z m -12.5683,-7.833 h 4.494 c 1.3828,0 2.0989,0.9884 2.0989,1.9768 0,0.9883 -0.7161,1.9767 -2.0989,1.9767 h -3.2593 v 3.2123 h 3.5803 c 1.3828,0 2.0989,0.9884 2.0989,1.9768 0,0.9883 -0.7161,1.9767 -2.0989,1.9767 h -4.815 z m 9.2844,6.0538 h -2.0248 v -1.2354 h 1.7779 c 1.0123,-0.8401 1.5309,-2.0509 1.5309,-3.2864 0,-2.298 -1.8025,-4.6207 -5.5558,-4.6207 h -8.8399 v 17.2967 h 9.1856 c 3.7779,0 5.6052,-2.3969 5.6052,-4.7442 0,-1.3096 -0.5679,-2.5698 -1.6791,-3.41 z"
                    fill="#000000"
                />
            </g>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m 2.5730403,18.610807 c 0.4312251,-0.492832 0.862444,-0.924062 1.3552786,-1.355292 1.971313,-1.663316 4.4354607,-2.58738 7.0227941,-2.58738 1.848166,0 3.696256,0.492831 5.359567,1.355295 0.431225,0.246412 0.985637,0.0616 1.232007,-0.36963 0.246448,-0.431232 0.06168,-0.985665 -0.369556,-1.232085 C 15.263372,13.374439 13.107242,12.82 10.951113,12.82 c -3.0185598,0 -5.913934,1.047273 -8.1932648,3.018619 -0.5544326,0.492832 -1.1088665,0.985662 -1.5400917,1.540105 -0.3080179,0.369629 -0.3080179,0.985672 0.1232061,1.293692 0.3696221,0.369623 0.9240509,0.308022 1.2320751,-0.0616 z m 13.3679277,3.38839 c 0.862452,0.985646 1.663312,1.786517 4.004299,2.648977 0.431225,0.184858 0.677594,0.739277 0.616002,1.108915 -0.184777,0.492824 -0.677674,0.739198 -1.170491,0.554417 -2.64895,-0.985648 -3.819363,-2.032969 -4.743487,-3.203397 -0.985637,-1.108912 -2.279315,-1.724926 -3.572994,-1.724926 -0.985637,0 -1.9096816,0.36964 -2.6489413,0.985649 -1.7865116,1.540146 -2.03292,4.06586 -0.5544339,6.098831 0.6776348,0.924056 1.7865129,2.032967 2.9570052,2.957022 0.924046,0.739276 2.032868,1.416882 3.141768,1.9713 0.492817,0.24645 0.677596,0.800867 0.431226,1.232099 -0.246449,0.492825 -0.800859,0.677682 -1.232087,0.431228 C 11.875156,34.443304 10.704665,33.704027 9.6574347,32.903159 8.3637488,31.917512 7.1316788,30.685412 6.3308263,29.576575 4.2363046,26.681145 4.6059306,23.169703 7.193278,21.013548 c 1.0472608,-0.924072 2.4025627,-1.416912 3.819426,-1.416912 1.848091,0 3.634587,0.862461 4.928264,2.402561 z m -0.862372,3.880921 c 0.739265,0.554496 1.971352,1.355365 4.312259,2.156157 0.49282,0.184858 0.800859,0.862463 0.616004,1.293692 -0.184779,0.431232 -0.677596,0.739276 -1.170414,0.554496 -1.909762,-0.616089 -3.449809,-1.355284 -4.743489,-2.341013 -0.800857,-0.616009 -1.540125,-1.355287 -2.279314,-2.279343 -0.308041,-0.431231 -0.924121,-0.554416 -1.355348,-0.123194 -0.369556,0.307967 -0.431227,0.862462 -0.123181,1.2321 0.739268,0.924054 1.478534,1.663251 2.464171,2.46412 1.04723,0.800872 2.340907,1.540146 3.572994,2.032972 0.215614,0.09243 0.384972,0.154022 0.554411,0.215615 v 0 0 c 0.169439,0.06159 0.338874,0.123182 0.554489,0.215616 0.431226,0.123182 0.616003,0.554417 0.492819,1.04724 -0.184856,0.492826 -0.677674,0.739275 -1.170493,0.616089 -1.663313,-0.492826 -3.572994,-1.540144 -5.11312,-2.710571 -1.047228,-0.800868 -2.0328657,-1.848187 -2.6489476,-2.649057 -1.0472453,-1.41688 -0.9240366,-2.956945 0.3080411,-4.004265 1.1704925,-1.047245 2.8337265,-0.800869 4.0042205,0.554416 0.55441,0.677681 0.985636,1.170507 1.724904,1.724924 z M 6.1460416,19.904493 c -3.203392,2.648965 -3.6346159,7.146133 -1.1704708,10.595981 0.5544287,0.739198 1.2936717,1.540067 2.094523,2.340938 0.3696169,0.308043 0.4312265,0.924052 0.06159,1.293692 -0.3696182,0.369637 -0.9240522,0.431232 -1.2936705,0.06159 C 4.9139523,33.334231 4.1131101,32.410177 3.4970757,31.547713 0.41688974,27.296997 1.0329268,21.752586 4.8523531,18.549196 6.5156496,17.132292 8.6717794,16.33144 10.88951,16.33144 c 2.032944,0 3.942624,0.616043 5.605937,1.786524 0.996461,0.71179 1.773649,1.569798 2.275266,2.123514 0.149583,0.165135 0.274636,0.303202 0.373682,0.402273 0.369634,0.369637 0.369634,0.924056 0,1.293692 -0.369634,0.36964 -0.924044,0.36964 -1.293678,0 -0.06315,-0.07219 -0.13424,-0.156279 -0.213276,-0.249721 -0.460427,-0.544605 -1.190194,-1.407683 -2.189302,-2.091253 -1.293677,-0.985671 -2.89532,-1.478505 -4.497036,-1.478505 -1.724905,0 -3.4497931,0.616042 -4.8050716,1.786527 z"
                fill="#000000"
                id="fingerprint"
                style={{ strokeWidth: 0.0778674 }}
            />
        </g>
        <defs>
            <clipPath id="clip0_2114_158">
                <rect width="90" height="18" fill="black" transform="translate(15 15)" />
            </clipPath>
        </defs>
    </svg>
);
